import axios from "utils/axios";
import { getCrmToken } from "./authService";

const readEnvFlag = (value, fallback = false) => {
  if (typeof value === "undefined" || value === null) return fallback;
  const str = String(value).toLowerCase();
  if (str === "true") return true;
  if (str === "false") return false;
  return fallback;
};

const getCompanyId = () => {
  try {
    // Prefer explicit env variable; otherwise try to read from token payload if available
    const fromEnv = import.meta?.env?.VITE_COMPANY_ID;
    if (fromEnv) return String(fromEnv);
    const token = localStorage.getItem("authToken");
    if (token) {
      const payloadBase64 = token.split(".")[1];
      const payload = JSON.parse(atob(payloadBase64));
      if (payload?.companyId) return String(payload.companyId);
    }
  } catch (_) {
    // noop
  }
  return undefined;
};

// Ensure a CRM bearer token is available in localStorage before protected calls
let crmTokenPromise;
const ensureCrmToken = async () => {
  const existing = localStorage.getItem("authToken");
  if (existing) return existing;
  if (!crmTokenPromise) {
    crmTokenPromise = (async () => {
      const result = await getCrmToken();
      if (!result?.success || !result?.token) {
        crmTokenPromise = undefined;
        throw new Error(result?.message || "Unable to acquire CRM token");
      }
      localStorage.setItem("authToken", result.token);
      return result.token;
    })();
  }
  try {
    const token = await crmTokenPromise;
    return token;
  } finally {
    crmTokenPromise = undefined;
  }
};

// ========================= API SHAPES =========================

const normalizeSubStage = (s, index = 0) => ({
  subStageId: s?.subStageId ?? s?.SubStageId ?? null,
  stageId: s?.stageId ?? s?.StageId ?? null,
  companyId: s?.companyId ?? s?.CompanyId,
  subStageName: s?.subStageName ?? s?.SubStageName ?? "",
  score: Number(s?.score ?? s?.Score ?? 0),
  subStageOrder: Number(s?.subStageOrder ?? s?.SubStageOrder ?? index + 1),
  isActive: s?.isActive ?? s?.IsActive ?? true,
  createdAt: s?.createdAt ?? s?.CreatedAt,
  createdBy: s?.createdBy ?? s?.CreatedBy,
  modifiedAt: s?.modifiedAt ?? s?.ModifiedAt,
  modifiedBy: s?.modifiedBy ?? s?.ModifiedBy,
});

const normalizeStage = (s, index = 0) => ({
  stageId: s?.stageId ?? s?.StageId ?? null,
  companyId: s?.companyId ?? s?.CompanyId,
  stageName: s?.stageName ?? s?.StageName ?? "",
  stageOrder: Number(s?.stageOrder ?? s?.StageOrder ?? index + 1),
  score: Number(s?.score ?? s?.Score ?? 0),
  isActive: s?.isActive ?? s?.IsActive ?? true,
  createdAt: s?.createdAt ?? s?.CreatedAt,
  createdBy: s?.createdBy ?? s?.CreatedBy,
  modifiedAt: s?.modifiedAt ?? s?.ModifiedAt,
  modifiedBy: s?.modifiedBy ?? s?.ModifiedBy,
  isSystemStage: s.IsSystemStage ?? false,
  isOrderLocked: s.IsOrderLocked ?? false,
  subStages: Array.isArray(s?.subStages)
    ? s.subStages.map(normalizeSubStage)
    : Array.isArray(s?.SubStages)
      ? s.SubStages.map(normalizeSubStage)
      : [],
});

/**
 * Fetch paginated list of stages and their substages
 * @param {Object} params
 * @param {number} [params.pageNumber=1]
 * @param {number} [params.pageSize=100]
 * @param {string} [params.companyId]
 * @param {string} [params.search]
 */
export const getStageAndSubStages = async (params = {}) => {
  const request = {
    pageNumber: params.pageNumber ?? 1,
    pageSize: params.pageSize ?? 10,
    companyId: params.companyId ?? getCompanyId(),
    search: params.search ?? "",
  };

  const response = await axios.post(
    "/api/LeadStage/GetStageAndSubStages",
    request,
    {
      showLoader: false, // disables GlobalLoader for this request
    }
  );
  const payload = response?.data;
  let raw = payload?.data ?? payload ?? [];
  // Backends may return JSON string in data; parse if needed
  if (typeof raw === "string") {
    try {
      raw = JSON.parse(raw);
    } catch (_) {
      raw = [];
    }
  }
  if (Array.isArray(raw)) {
    return raw.map((s, i) => normalizeStage(s, i));
  }
  if (raw?.items && Array.isArray(raw.items)) {
    return raw.items.map((s, i) => normalizeStage(s, i));
  }
  return [];
};


export const getStagesWithSubStagesForBulkUpload = async (params = {}) => {
  const request = {
    pageNumber: params.pageNumber ?? 1,
    pageSize: params.pageSize ?? 10,
    companyId: params.companyId ?? getCompanyId(),
    search: params.search ?? "",
  };

  const response = await axios.post(
    "api/Leads/GetStagesWithSubStagesForBulkUpload",
    request,
    {
      showLoader: false, // disables GlobalLoader for this request
    }
  );
  const payload = response?.data;
  let raw = payload?.data ?? payload ?? [];
  // Backends may return JSON string in data; parse if needed
  if (typeof raw === "string") {
    try {
      raw = JSON.parse(raw);
    } catch (_) {
      raw = [];
    }
  }
  if (Array.isArray(raw)) {
    return raw.map((s, i) => normalizeStage(s, i));
  }
  if (raw?.items && Array.isArray(raw.items)) {
    return raw.items.map((s, i) => normalizeStage(s, i));
  }
  return [];
};





/**
 * Fetch a single stage and optionally its substages by id
 * @param {number} stageId
 * @param {boolean} [getSubstage=true]
 */
export const getStageAndSubstagesById = async (stageId, getSubstage = true) => {
  const response = await axios.get(
    `/api/LeadStage/GetStageAndSubstagesById/${stageId}/${getSubstage}`,
  );
  const payload = response?.data;
  let raw = payload?.data ?? payload ?? null;
  if (!raw) return null;
  if (typeof raw === "string") {
    try {
      raw = JSON.parse(raw);
    } catch (_) {
      /* ignore */
    }
  }
  return normalizeStage(raw);
};

/**
 * Upsert a stage
 * @param {Object} stage
 * @param {number} [stage.stageId=0]
 * @param {string} stage.stageName
 * @param {number} stage.stageOrder
 * @param {boolean} [stage.isActive=true]
 */
export const upsertStage = async (stage) => {
  await ensureCrmToken();
  const nowIso = new Date().toISOString();
  const companyId = stage.companyId ?? getCompanyId();
  const userId =
    import.meta?.env?.VITE_USER_ID || "3fa85f64-5717-4562-b3fc-2c963f66afa6";

  const body = {
    companyId,
    stageName: stage.stageName?.trim() ?? "",
    stageOrder: Number(stage.stageOrder ?? 0),
    score: Number(stage.score ?? 0),
    isActive: readEnvFlag(stage.isActive, true),
    createdBy: stage.createdBy ?? userId,
    modifiedBy: userId,
    createdAt: stage.createdAt ?? nowIso,
    modifiedAt: nowIso,
  };

  // For update
  if (stage.stageId && stage.stageId !== 0 && stage.stageId !== "0") {
    body.stageId = stage.stageId;
  }

  const response = await axios.post("/api/LeadStage/UpsertStage", body);
  const payload = response?.data;

  if (!payload) {
    throw Error("Invalid server response");
  }

  if (payload?.statusCode === 404) {
    throw Error(payload?.message ?? "Some error occurred.");
  }

  // ✅ Data might be a string (stringified JSON)
  let data = payload?.data;
  if (typeof data === "string") {
    try {
      data = JSON.parse(data);
    } catch (err) {
      console.error("Failed to parse data JSON:", err);
      throw Error("Invalid data format received");
    }
  }

  // Normalize ID from backend keys
  const returnedId =
    data?.stageId ??
    data?.StageId ??
    data?.id ??
    data?.Id ??
    body.stageId;

  return { ...data, stageId: returnedId };
};


/**
 * Upsert a substage
 * @param {Object} sub
 * @param {number} [sub.subStageId=0]
 * @param {number} sub.stageId
 * @param {string} sub.subStageName
 * @param {number} sub.subStageOrder
 * @param {boolean} [sub.isActive=true]
 */
export const upsertSubStage = async (sub) => {
  // Ensure auth for protected endpoint
  await ensureCrmToken();
  const nowIso = new Date().toISOString();
  const companyId = sub.companyId ?? getCompanyId();
  const userId =
    import.meta?.env?.VITE_USER_ID || "3fa85f64-5717-4562-b3fc-2c963f66afa6";
  const body = {
    stageId: sub.stageId,
    companyId,
    score: Number(sub.score ?? 0),
    subStageName: sub.subStageName?.trim() ?? "",
    subStageOrder: Number(sub.subStageOrder ?? 0),
    isActive: readEnvFlag(sub.isActive, true),
    createdBy: sub.createdBy ?? userId,
    modifiedBy: userId,
    createdAt: sub.createdAt ?? nowIso,
    modifiedAt: nowIso,
  };
  if (sub.subStageId && sub.subStageId !== 0 && sub.subStageId !== "0") {
    body.subStageId = sub.subStageId;
  }
  const response = await axios.post("/api/LeadStage/UpsertSubStage", body);
  const payload = response?.data;
  if (payload?.statusCode === 404) {
    throw Error(payload?.message ?? "Some error occured.");
  }

  const data = payload?.data ?? payload ?? {};
  const returnedId = data?.subStageId ?? data?.id ?? data?.Id ?? undefined;
  return { ...data, subStageId: returnedId ?? body.subStageId };

};

/**
 * Delete a stage and all its substages or subStages
 * @param {number} stageId - The ID of the stage or subStage to delete
 */
export const deleteStageAndSubStages = async (stageId) => {
  // Ensure auth for protected endpoint
  await ensureCrmToken();

  const response = await axios.delete(
    `/api/LeadStage/DeleteStageAndSubStages/${stageId}`,
  );
  return response?.data;
};

/**
 * Delete a substage
 * @param {number} subStageId - The ID of the substage to delete
 */
export const deleteSubStage = async (subStageId) => {
  // Ensure auth for protected endpoint
  await ensureCrmToken();

  const response = await axios.delete(
    `/api/LeadStage/DeleteSubStage/${subStageId}`,
  );
  return response?.data;
};

export default {
  getStageAndSubStages,
  getStageAndSubstagesById,
  upsertStage,
  upsertSubStage,
  deleteStageAndSubStages,
  deleteSubStage,
};
